import React, { useCallback, useState } from 'react';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { PanelSplitter } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { RightPanel } from '@atlassian/jira-navigation-system/src/index.tsx';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { previewPanelStateResource } from '@atlassian/jira-navigation-apps-resources/src/services/preview-panel/index.tsx';
import { useLayoutStoreActions } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/store/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { PreviewPanelQuery } from './__generated__/PreviewPanelQuery.graphql';

import { messages } from './messages.tsx';

const PreviewPanelInternal = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<PreviewPanelQuery>;
}) => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const [, { setPreviewPanelState }] = useLayoutStoreActions();
	const [useLocalWidth, setUseLocalWidth] = useState(false);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);
			setPreviewPanelState({
				width: finalWidth,
			});
			setUseLocalWidth(true);
		},
		[panel, setPreviewPanelState],
	);

	const data = usePreloadedQuery<PreviewPanelQuery>(
		graphql`
			query PreviewPanelQuery($cloudId: ID!) {
				jira {
					navigationUIState(cloudId: $cloudId) {
						previewPanel {
							width
						}
					}
				}
			}
		`,
		queryReference,
	);

	if (panel === null) {
		return null;
	}

	const { content: PanelContent } = panel;

	const previewPanelWidth = useLocalWidth
		? panel?.width
		: (data?.jira?.navigationUIState?.previewPanel?.width ?? panel?.width);

	return (
		<RightPanel width={previewPanelWidth} testId="preview-panels.preview-panel">
			<PanelContent />
			<PanelSplitter
				label={formatMessage(messages.previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

const PreviewPanelWithoutWidthPersistance = () => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const [, { setPreviewPanelState }] = useLayoutStoreActions();

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);
			setPreviewPanelState({
				width: finalWidth,
			});
		},
		[panel, setPreviewPanelState],
	);

	if (panel === null) {
		return null;
	}

	const { content: PanelContent } = panel;

	const previewPanelWidth = panel?.width;

	return (
		<RightPanel width={previewPanelWidth} testId="preview-panels.preview-panel">
			<PanelContent />
			<PanelSplitter
				label={formatMessage(messages.previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

export const PreviewPanel = () => {
	if (fg('preview_panel_width_persistance')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { queryReference } = useRelayResource(previewPanelStateResource);

		if (!queryReference) {
			return null;
		}

		return (
			<JSErrorBoundary
				id="preview-panel-width-query"
				packageName="preview-panels"
				teamName="Gryffindor"
				fallback={() => <PreviewPanelWithoutWidthPersistance />}
			>
				<Placeholder
					name="preview-panel-width-query"
					fallback={<PreviewPanelWithoutWidthPersistance />}
				>
					<PreviewPanelInternal queryReference={queryReference} />
				</Placeholder>
			</JSErrorBoundary>
		);
	}

	return <PreviewPanelWithoutWidthPersistance />;
};
